'use client'
// Types
import type { GridColDef, GridSortCellParams, GridRowSelectionModel } from '@mui/x-data-grid'
// Components
// Hooks
import { DataGrid, useGridApiRef } from '@mui/x-data-grid'
import React from 'react'
import { usePathname } from 'next/navigation'
import {
  curateRows,
  translateFromTableToDataGrid,
  getColumnVisibilityModel,
  getDataGridSx,
  useTableSort,
  useColumnResize,
} from './utils'
import { Skeleton } from '@/CommonComponents'

export type IColumnKey<T> = 'aux' | keyof T
export type IColumn<T> = {
  label: string
  key: IColumnKey<T>
  button?: { label: string; action: (row: T) => void }
  width?: number
  customRender?: (row: T) => React.ReactNode | string
  sortComparator?: (
    v1: T[keyof T],
    v2: T[keyof T],
    cellParams1: GridSortCellParams<T>,
    cellParams2: GridSortCellParams<T>,
  ) => number
  valueGetter?: (cellValue: T[keyof T]) => string
}
interface ITableProps<T> {
  rows: Array<T>
  columnDefinition: IColumn<T>[]
  paginated?: boolean
  onRowClick?: (data: { row: T }) => void
  onSelectionChange?: (selectionIds: number[]) => void
  selectable?: boolean
  hiddenColumns?: IColumnKey<T>[]
  isLoading?: boolean
}

export default function Table<T>({
  rows,
  columnDefinition,
  paginated = true,
  onRowClick,
  onSelectionChange,
  selectable = true,
  hiddenColumns = [],
  isLoading = false,
}: ITableProps<T>) {
  const pathname = usePathname()
  const curatedRows = curateRows<T>(rows)
  const apiRef = useGridApiRef()
  const dataGridColumns: GridColDef[] = translateFromTableToDataGrid<T>(columnDefinition)
  const { sortModel, onSortModelChange } = useTableSort(pathname)
  useColumnResize(apiRef, curatedRows)
  const PAGE_SIZE = 50

  const getRowHeight = () => 'auto' as const
  if (isLoading) {
    return <Skeleton variant="rectangular" height={500} />
  }

  const dataGridProps = {
    apiRef,
    localeText: {
      noRowsLabel: 'Sin filas',
      toolbarDensity: 'Densidad',
      toolbarExport: 'Exportar',
      MuiTablePagination: {
        labelRowsPerPage: 'Filas por página',
        labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
          `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`,
      },
    },
    onRowClick,
    onRowSelectionModelChange: (selectionIds: GridRowSelectionModel) => {
      if (selectionIds.every((id) => typeof id === 'number')) {
        onSelectionChange?.(selectionIds as number[])
      }
    },
    sortModel,
    onSortModelChange,
    rows: curatedRows,
    columns: dataGridColumns,
    autosizeOnMount: true,
    getRowHeight,
    getEstimatedRowHeight: () => 50,
    checkboxSelection: selectable,
    columnVisibilityModel: getColumnVisibilityModel(hiddenColumns),
    disableRowSelectionOnClick: true,
    initialState: {
      pagination: { paginationModel: { page: 0, pageSize: PAGE_SIZE } },
      columns: { columnVisibilityModel: getColumnVisibilityModel(hiddenColumns) },
    },
    sx: getDataGridSx(selectable),
    pageSizeOptions: [5, 10, 50, 100],
    hideFooter: !paginated || rows.length <= PAGE_SIZE,
    disableColumnMenu: false,
    disableColumnFilter: false,
    disableColumnSelector: false,
  }

  return <DataGrid {...dataGridProps} />
}
